// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { useDisclosure } from '@mantine/hooks';
import type { Encounter, Patient, Practitioner, Reference, Task } from '@medplum/fhirtypes';
import { Loading, useResource } from '@medplum/react';
import type { JSX } from 'react';
import type { EncounterChartHook } from '../../hooks/useEncounterChart';
import { useEncounterChart } from '../../hooks/useEncounterChart';
import { useEncounterNoteController } from '../../hooks/useEncounterNoteController';
import { useRole } from '../../hooks/useRole';
import { hasPermission } from '../../utils/permissions';
import { ConcludeSessionModal } from '../appointment/ConcludeSessionModal';
import { EncounterNoteRefactoredShell } from './EncounterNoteRefactoredShell';
import { ScheduleNextAppointmentModal } from './ScheduleNextAppointmentModal';

export interface EncounterChartProps {
  encounter: Encounter | Reference<Encounter>;
}

interface EncounterChartLoadedProps {
  appointment: EncounterChartHook['appointment'];
  canConduct: boolean;
  canSchedule: boolean;
  chargeItems: EncounterChartHook['chargeItems'];
  checklistItems: EncounterChartHook['checklistItems'];
  claim: EncounterChartHook['claim'];
  clinicalImpression: EncounterChartHook['clinicalImpression'];
  encounter: Encounter;
  patient: Patient;
  practitioner: EncounterChartHook['practitioner'];
  setChargeItems: EncounterChartHook['setChargeItems'];
  setClaim: EncounterChartHook['setClaim'];
  setClinicalImpression: EncounterChartHook['setClinicalImpression'];
  setEncounter: EncounterChartHook['setEncounter'];
  setPractitioner: EncounterChartHook['setPractitioner'];
  setTasks: EncounterChartHook['setTasks'];
  tasks: EncounterChartHook['tasks'];
}

const EncounterChartRefactoredLoaded = ({
  appointment,
  canConduct,
  canSchedule,
  chargeItems,
  checklistItems,
  claim,
  clinicalImpression,
  encounter,
  patient,
  practitioner,
  setChargeItems,
  setClaim,
  setClinicalImpression,
  setEncounter,
  setPractitioner,
  setTasks,
  tasks,
}: EncounterChartLoadedProps): JSX.Element => {
  const [concludeOpened, { open: openConclude, close: closeConclude }] = useDisclosure(false);
  const [scheduleOpened, { open: openSchedule, close: closeSchedule }] = useDisclosure(false);
  const controller = useEncounterNoteController({
    encounter,
    appointment,
    patient,
    tasks,
    clinicalImpression,
    setClinicalImpression,
    setEncounter,
    setTasks,
  });

  return (
    <>
      <EncounterNoteRefactoredShell
        appointment={appointment}
        canConduct={canConduct}
        canSchedule={canSchedule}
        chargeItems={chargeItems}
        checklistItems={checklistItems}
        claim={claim}
        controller={controller}
        encounter={encounter}
        onConclude={openConclude}
        onOpenSchedule={openSchedule}
        patient={patient}
        practitioner={practitioner}
        setChargeItems={setChargeItems}
        setClaim={setClaim}
        setEncounter={setEncounter}
        setPractitioner={setPractitioner}
      />

      <ConcludeSessionModal
        opened={concludeOpened}
        onClose={closeConclude}
        encounter={encounter}
        appointment={appointment}
        onBeforeConclude={controller.saveDraft}
        onConcluded={(concluded) => {
          setEncounter(concluded);
          closeConclude();
        }}
      />

      <ScheduleNextAppointmentModal opened={scheduleOpened} onClose={closeSchedule} patient={patient} />
    </>
  );
};

export const EncounterChart = (props: EncounterChartProps): JSX.Element => {
  const { encounter: encounterProp } = props;
  const encounterResource = useResource(encounterProp);
  const patientReference = encounterResource?.subject as Reference<Patient> | undefined;
  const patientResource = useResource(patientReference);
  const {
    encounter,
    claim,
    practitioner,
    tasks,
    checklistItems,
    clinicalImpression,
    clinicalImpressionLoaded,
    chargeItems,
    appointment,
    setEncounter,
    setClaim,
    setClinicalImpression,
    setPractitioner,
    setTasks,
    setChargeItems,
  } = useEncounterChart(encounterProp, patientReference);
  const { role, isAdmin } = useRole();
  const canConduct = hasPermission({ role, permission: 'conduct-appointment', isAdmin });
  const canSchedule = hasPermission({ role, permission: 'schedule-appointment', isAdmin });

  if (!patientResource || !encounter || !clinicalImpressionLoaded) {
    return <Loading />;
  }

  return (
    <EncounterChartRefactoredLoaded
      appointment={appointment}
      canConduct={canConduct}
      canSchedule={canSchedule}
      chargeItems={chargeItems}
      checklistItems={checklistItems}
      claim={claim}
      clinicalImpression={clinicalImpression}
      encounter={encounter}
      patient={patientResource}
      practitioner={practitioner}
      setChargeItems={setChargeItems}
      setClaim={setClaim}
      setClinicalImpression={setClinicalImpression}
      setEncounter={setEncounter}
      setPractitioner={setPractitioner}
      setTasks={setTasks}
      tasks={tasks}
    />
  );
};
